import type {
  ColumnDef,
  Table,
} from "@tanstack/react-table"

import type { InstallmentsStatusType } from "@/helpers"
import { getStatusInfo } from "@/helpers"
import { formatMoney } from "@/helpers/format"
import { cn } from "@/lib/utils"
import type { TransactionInstallmentType } from "@/types/transactions"
interface Transactions extends TransactionInstallmentType {
  numberOfInstallments: number
  transactionStatus: InstallmentsStatusType
}
const getFooterTotal = (
  table: Table<Transactions>,
  columnId: keyof Transactions
) => {
  return table.getRowModel().rows.reduce((acc, row) => {
    const value =
      row.original[columnId as keyof typeof row.original]
    return acc + (typeof value === "number" ? value : 0)
  }, 0)
}
export const transactionColumns: ColumnDef<Transactions>[] =
  [
    {
      accessorKey: "status",
      header: "Status",
      enableHiding: false,
      cell({ row }) {
        const { transactionStatus, date } = row.original
        const { bgColor, text } = getStatusInfo(
          transactionStatus as InstallmentsStatusType
        )
        return (
          <div>
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "size-4 rounded-full",
                  bgColor
                )}
              />
              <span className="font-semibold leading-none">
                {text}
              </span>
            </div>
            <small className="ml-6 leading-none text-muted-foreground">
              efetuado em {date}
            </small>
          </div>
        )
      },
      footer() {
        return (
          <div className="bg-r text-base font-semibold">
            Total
          </div>
        )
      },
    },
    {
      accessorKey: "installmentNumber",
      header() {
        return <div className="text-center">Parcela</div>
      },
      cell: ({ row }) => {
        const { installmentNumber, numberOfInstallments } =
          row.original
        return (
          <div className="text-center">
            {installmentNumber}/{numberOfInstallments}
          </div>
        )
      },
      footer: ({ table }) => {
        const total =
          table.getRowModel().rows[0].original
            .numberOfInstallments
        return <div className="text-center">{total}</div>
      },
    },
    {
      accessorKey: "value",
      header() {
        return (
          <div className="text-nowrap text-end">
            Valor bruto{" "}
          </div>
        )
      },
      cell: ({ row }) => {
        const { value } = row.original
        return (
          <div className="text-end">
            {formatMoney(value / 100)}
          </div>
        )
      },
      footer({ table }) {
        const total = getFooterTotal(table, "value")
        return (
          <div className="text-end">
            {formatMoney(total / 100)}
          </div>
        )
      },
    },
    {
      accessorKey: "cashbackValue",
      header() {
        return <div className="text-end">Cashback</div>
      },
      cell: ({ row }) => {
        const { cashbackValue } = row.original
        return (
          <div className="text-end">
            {formatMoney(cashbackValue / 100)}
          </div>
        )
      },
      footer({ table }) {
        const total = getFooterTotal(table, "cashbackValue")
        return (
          <div className="text-end">
            {formatMoney(total / 100)}
          </div>
        )
      },
    },
    {
      accessorKey: "commissionValue",
      header() {
        return <div className="text-end">Taxa </div>
      },
      cell: ({ row }) => {
        const { commissionValue } = row.original
        return (
          <div className="text-end">
            {formatMoney(commissionValue / 100)}
          </div>
        )
      },
      footer({ table }) {
        const total = getFooterTotal(
          table,
          "commissionValue"
        )
        return (
          <div className="text-end">
            {formatMoney(total / 100)}
          </div>
        )
      },
    },
    {
      accessorKey: "netValue",
      header() {
        return (
          <div className="text-nowrap text-end">
            Valor a receber{" "}
          </div>
        )
      },
      cell: ({ row }) => {
        const { netValue } = row.original
        return (
          <div className="text-end">
            {formatMoney(netValue / 100)}
          </div>
        )
      },
      footer({ table }) {
        const total = getFooterTotal(table, "netValue")
        return (
          <div className="text-end">
            {formatMoney(total / 100)}
          </div>
        )
      },
    },
  ]
